import React from 'react';

import { InfoIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { formatDateTime } from '../../../dateFormat';

import {
  AdvancedTable,
  Button,
  TextWithTooltip,
  useIsMobile,
} from '@sandtech-hub-monorepo/ui-kit';
import {
  GetProgramEnrollmentPaymentPlansQuery,
  GetSemesterPaymentPlansQuery,
  IncompleteTransactionStatus,
} from '@sandtech-hub-monorepo/ehub-client';

// Support both central-hub and set-edu payment plan types
type CentralHubIncompleteTransaction = NonNullable<
  NonNullable<
    NonNullable<GetProgramEnrollmentPaymentPlansQuery['viewer']>['paymentPlans']
  >[number]['incompleteTransactions'][number]
>;

type SetEduIncompleteTransaction = NonNullable<
  GetSemesterPaymentPlansQuery['viewer']['paymentPlans'][number]['incompleteTransactions'][number]
>;

type IncompleteTransaction =
  | CentralHubIncompleteTransaction
  | SetEduIncompleteTransaction;

interface Props {
  incompleteTransactions: IncompleteTransaction[];
}

const getClassNameByIncompleteTransactionStatus = (
  status: IncompleteTransactionStatus
) => {
  switch (status) {
    case IncompleteTransactionStatus.Canceled:
    case IncompleteTransactionStatus.Declined:
    case IncompleteTransactionStatus.Expired:
    case IncompleteTransactionStatus.Failed:
      return 'text-destructive';

    case IncompleteTransactionStatus.Abandoned:
    case IncompleteTransactionStatus.Initialized:
    case IncompleteTransactionStatus.Pending:
      return 'text-warning';
    default:
      return 'text-foreground';
  }
};

export const IncompleteTransactionsSection = ({
  incompleteTransactions,
}: Props) => {
  const mobile = useIsMobile();

  if (!incompleteTransactions?.length) {
    return null;
  }

  return (
    <div>
      <h1 className="text-lg font-semibold my-8">Incomplete Transactions</h1>
      <AdvancedTable
        pageSize={10}
        entries={incompleteTransactions}
        columns={[
          {
            key: 'date',
            name: 'Date',
            cell: ({ createdAt }: IncompleteTransaction) =>
              formatDateTime(createdAt),
          },
          {
            key: 'amount',
            name: 'Amount',
            cell: ({ amount }: IncompleteTransaction) => (
              <span className="text-tertiary">
                {amount != null ? `USD ${(amount / 100).toFixed(2)}` : '—'}
              </span>
            ),
          },
          {
            key: 'description',
            name: 'Description',
            cell: ({ description }: IncompleteTransaction) => description,
          },
          {
            key: 'status',
            name: 'Status',
            cell: ({ status }: IncompleteTransaction) => (
              <span
                className={`font-semibold ${getClassNameByIncompleteTransactionStatus(
                  status
                )}`}
              >
                {status}
              </span>
            ),
          },
          mobile
            ? null
            : {
                key: 'reason',
                name: (
                  <TextWithTooltip
                    truncatedText={
                      <div className="flex gap-1 items-center">
                        Reason <InfoIcon size={16} />
                      </div>
                    }
                    textToDisplay="These are the status and error codes returned by our payment gateway partners"
                    showCopyButton={false}
                  />
                ),
                cell: ({ status, reason, icon }: IncompleteTransaction) => (
                  <div className="flex gap-2 items-center justify-between">
                    <p
                      className={getClassNameByIncompleteTransactionStatus(
                        status
                      )}
                    >
                      {reason}
                    </p>
                    {icon && (
                      <img
                        src={icon}
                        alt="payment-method-icon"
                        className="h-4"
                      />
                    )}
                  </div>
                ),
              },
          {
            key: 'actions',
            name: '',
            cell: ({ retryUrl }: IncompleteTransaction) => (
              <div className="flex">
                {retryUrl && (
                  <Link to={retryUrl} target="_blank" rel="noopener noreferrer">
                    <Button variant="link">
                      <span className="text-primary font-semibold">Retry</span>
                    </Button>
                  </Link>
                )}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};
